import {
    Box,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography
} from '@mui/material'
import GenericTabs from '../genericTabs/genericTabs'
import React, { useEffect, useMemo, useState } from 'react'
import NoTradingAccountEmptyState from '../tradingAccountStatus/NoTradingAccountEmptyState'
import { useSelector } from 'react-redux'
import { Order } from '../../proto/generated/order'
import OrderIcon from '../../assets/icons/Order'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { selectOrdersHistory, selectOrdersHistoryLoaded } from '../../selectors/orders'
import SearchIcon from '../../assets/icons/SearchIcon'
import ClearIcon from '../../assets/icons/ClearIcon'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import OrdersTableScrollArea from './OrdersTableScrollArea'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import TradingAccountSelector from '../tradingAccountStatus/TradingAccountSelector'
import { filterOrdersForOrdersWidgetTab, type OrdersWidgetTableView } from '../../util/orders'
import { OrdersNavButtonsDetailed } from '../postionsWidget'

export const navItems = [
    {
        key: OrdersNavButtonsDetailed.OPEN,
        label: 'Open'
    },
    {
        key: OrdersNavButtonsDetailed.COMPLETE,
        label: 'Complete'
    },
    {
        key: OrdersNavButtonsDetailed.REJECTED,
        label: 'Rejected'
    },
    {
        key: OrdersNavButtonsDetailed.CANCELLED,
        label: 'Cancelled'
    },
    {
        key: OrdersNavButtonsDetailed.ALL,
        label: 'All'
    },
]
const OrdersWidget = () => {
    const [tableViewType, setTableViewType] = useState<OrdersNavButtonsDetailed>(OrdersNavButtonsDetailed.OPEN)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const ordersData = useSelector(selectOrdersHistory)
    const ordersLoaded = useSelector(selectOrdersHistoryLoaded)
    const [filterOrdersData, setFilterOrdersData] = useState<Order[]>([])
    const [searchText, setSearchText] = useState('')
    const [showSearch, setShowSearch] = useState(false)
    const getFilteredOrders = (orders: Order[]) => {
        if (showSearch && searchText.trim().length > 0) {
            return orders;
        }
        return filterOrdersForOrdersWidgetTab(orders, tableViewType as OrdersWidgetTableView);
    };
    const visibleOrders = getFilteredOrders(filterOrdersData);
    // See PositionsWidget: the async symbol filter must produce rows before a non-empty history counts as ready.
    const searching = showSearch && searchText.trim().length > 0
    const ordersReady = ordersLoaded && (!ordersData?.length || filterOrdersData.length > 0 || searching)
    const orderDataSecKeys = useMemo(() => {
        const data: string[] = []
        ordersData?.forEach((order) => {
            const secKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? '')
            data.push(secKey)
        })
        return data
    }, [ordersData])

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            if (!ordersData?.length) {
                setFilterOrdersData([])
                return
            }
            const results = await Promise.all(
                ordersData.map((data, i) =>
                    getSecurityAsync(orderDataSecKeys[i]).then((security) =>
                        (security?.symbol)?.toLowerCase()?.includes(searchText.trim().toLowerCase())
                    )
                )
            )
            if (!cancelled) {
                setFilterOrdersData(ordersData.filter((_, i) => results[i]))
            }
        }
        run()
        return () => { cancelled = true }
    }, [orderDataSecKeys, ordersData, searchText])

    return (
        <Paper sx={{ display: 'flex', height: '100%', flexDirection: 'column', borderRadius: '6px' }}>
            <Grid container>
                <Grid container px={'8px'} py={0}
                    justifyContent={'space-between'}
                    alignItems={'center'}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <OrderIcon sx={{ color: (theme) => theme.palette.text.secondary }} />
                        <Typography variant={'body1'}>Orders</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TradingAccountSelector compact />
                    </Box>
                </Grid>
                <Divider style={{ width: '100%' }} />
            </Grid>
            {
                primaryTradingAccount ?
                    (<>
                        <Grid container justifyContent={'space-between'} style={{ padding: '10px' }}>
                            <Grid item>
                                {!showSearch && ordersData?.length > 0 && (
                                    <GenericTabs<OrdersNavButtonsDetailed>
                                        items={navItems}
                                        onChange={(val) => {
                                            AnalyticsManager.logEvent(`${val === OrdersNavButtonsDetailed.OPEN ? 'Open' : 'All'} Tab Clicked`, { source: 'Orders' })
                                            if (val) setTableViewType(val)
                                        }}
                                        value={tableViewType}
                                        tabProps={{ sx: { minWidth: 60 } }}
                                        sx={{ height: 32, pr: 1 }}
                                    />)}
                            </Grid>
                            {!!(ordersData && ordersData?.length > 0) && (

                                (!showSearch ? (
                                    <Grid item >
                                        <IconButton onClick={() => setShowSearch(true)} size="small">
                                            <SearchIcon />
                                        </IconButton>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            autoFocus
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Search Symbol"
                                            InputProps={{
                                                sx: { height: 32, pl: 1 },
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSearchText('');
                                                                setShowSearch(false);
                                                            }}
                                                        >
                                                            <ClearIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Grid>
                                )))
                            }
                        </Grid>
                        <OrdersTableScrollArea
                            orders={ordersData?.length === 0 ? [] : visibleOrders}
                            tabType={tableViewType}
                            emptyText={
                                showSearch && searchText.trim().length > 0
                                    ? 'No orders match your search.'
                                    : tableViewType === OrdersNavButtonsDetailed.OPEN
                                        ? 'Your open orders will appear here.'
                                        : 'Your order history will appear here.'
                            }
                            showEmptyPreview={!searching}
                            loaded={ordersReady}
                            variant="widget"
                        />
                    </>) : (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <NoTradingAccountEmptyState subject="orders" preview="orders" />
                        </Box>
                    )
            }
        </Paper>
    )
}

export default OrdersWidget